import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import TrainModal from "../TrainModal/TrainModal";

import {
  TrainContainer,
  TrainGrid,
  TrainModelCard,
  CardTitle,
  ModelCard,
  ModelTitle,
  EmptyState,
  Button,
} from "./Train.styles";

import { ReactComponent as AiSvg } from "../../assets/icons/aiGenerate.svg";
import { ReactComponent as PreparingSvg } from "../../assets/icons/preparing.svg";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import {
  selectTrainModels,
  selectCreateTrainModelLoading,
} from "../../store/slices/model/slice";
import {
  getTrainModels,
  createTrainModel,
} from "../../store/slices/model/asyncThunks";
import TrainSkeletonLoader from "../utils/TrainSkeletonLoader";
import {
  TRAIN_MODEL_STATUS,
  TRAIN_MODEL_STATUS_TEXT,
} from "../../utils/constants";

const Train = ({ onLoginClick }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isTrainModalOpen, setIsTrainModalOpen] = useState(false);
  const isLoggedIn = useSelector(selectLoggedIn);
  const trainModels = useSelector(selectTrainModels);
  const createTrainModelLoading = useSelector(selectCreateTrainModelLoading);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(getTrainModels());
    }
  }, [isLoggedIn, dispatch]);

  const handleTrainModelClick = () => {
    setIsTrainModalOpen(true);
  };

  const handleCloseTrainModal = () => {
    setIsTrainModalOpen(false);
  };

  const handleContinue = async (modelName) => {
    try {
      const result = await dispatch(
        createTrainModel({ name: modelName })
      ).unwrap();

      const newModel = result.data.trainModel;
      dispatch(getTrainModels());

      setIsTrainModalOpen(false);

      navigate(`/train/${newModel.id}`);
    } catch (error) {
      console.log("Failed to create train model:", error);
    }
  };

  const handleModelClick = (model) => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);

    if (
      [
        TRAIN_MODEL_STATUS.IN_PROGRESS,
        TRAIN_MODEL_STATUS.SUBMITTED_FOR_TRAINING,
        TRAIN_MODEL_STATUS.TRAINING,
        TRAIN_MODEL_STATUS.TRAINING_COMPLETED,
        TRAIN_MODEL_STATUS.GENERATING_COLLECTIONS,
      ].includes(model.status)
    ) {
      navigate(`/train/${model._id}`);
    } else if (
      [TRAIN_MODEL_STATUS.COLLECTIONS_CREATED].includes(model.status)
    ) {
      navigate(`/train/select-cover/${model._id}`);
    } else if (
      [
        TRAIN_MODEL_STATUS.COMPLETED,
        TRAIN_MODEL_STATUS.SUBMITTED_FOR_REVIEW,
        TRAIN_MODEL_STATUS.ACCEPTED,
        TRAIN_MODEL_STATUS.DECLINED,
      ].includes(model.status)
    ) {
      navigate(`/train/completed/${model._id}`);
    } else {
      navigate(`/train/${model._id}`);
    }
  };

  if (isLoggedIn === null) {
    return null;
  }

  if (isLoggedIn === false) {
    return (
      <TrainContainer>
        <EmptyState>
          <h3>Login Required</h3>
          <p>You need to be logged in to view training models.</p>
          <Button onClick={onLoginClick}>Login</Button>
        </EmptyState>
      </TrainContainer>
    );
  }

  if (trainModels.loading) {
    return (
      <TrainContainer>
        <TrainSkeletonLoader count={3} />
      </TrainContainer>
    );
  }

  return (
    <TrainContainer>
      <TrainGrid>
        <TrainModelCard onClick={handleTrainModelClick}>
          <AiSvg />

          <CardTitle>Train your own model</CardTitle>
        </TrainModelCard>

        {trainModels.data.map((model, i) => (
          <ModelCard
            key={model._id + i}
            onClick={() => handleModelClick(model)}
          >
            {[
              TRAIN_MODEL_STATUS.SUBMITTED_FOR_TRAINING,
              TRAIN_MODEL_STATUS.TRAINING,
              TRAIN_MODEL_STATUS.TRAINING_COMPLETED,
              TRAIN_MODEL_STATUS.GENERATING_COLLECTIONS,
            ].includes(model.status) && <PreparingSvg />}

            <CardTitle>{TRAIN_MODEL_STATUS_TEXT[model.status]}</CardTitle>

            <ModelTitle>{model.name}</ModelTitle>
          </ModelCard>
        ))}
      </TrainGrid>

      <TrainModal
        isOpen={isTrainModalOpen}
        onClose={handleCloseTrainModal}
        onContinue={handleContinue}
        loading={createTrainModelLoading}
      />
    </TrainContainer>
  );
};

export default Train;
